import { Order, OrderItem } from "@/api/SolarCloudApi";

enum orderUnits {
  WATTS = "watts",
  KILOWATTS = "kilowatts",
}

type PanelCapacityProps = {
  order: Order;
  units?: number;
};

function panelCapacity(order: Order, units = orderUnits.KILOWATTS) {
  if (!order || !order.items) {
    return 0;
  }

  const totalCapcity = order.items.reduce((total, item) => {
    if (item.units) {
      return total + (item.units || 0);
    }
    return total;
  }, 0);

  const unitString = units === orderUnits.KILOWATTS ? "kW" : "W";

  return `${
    totalCapcity / (units === orderUnits.KILOWATTS ? 1000 : 1)
  }${unitString}`;
}

export function calculateOrderItemPrice(item: OrderItem) {
  return item.units! * item.product?.wattPriceCurrent!;
}

export function getOrderDiscountPercentage(
  order?: Order,
  items?: OrderItem[]
): number {
  if (!order) return 0;

  const currentItems = items ?? order.items ?? [];
  const totalPanels = currentItems.reduce((sum, item) => {
    const wattPanelSize = item.product?.wattPanelSize || 100;
    return sum + (item.units || 0) / wattPanelSize;
  }, 0);

  if (order.discount) {
    if (
      order.discount.minPanels != null &&
      totalPanels < order.discount.minPanels
    ) {
      return 0;
    }
    if (order.discount.percentage != null) {
      return order.discount.percentage;
    }
  }

  return (order.fullPrice ?? 0) > 0
    ? ((order.totalDiscount ?? 0) / order.fullPrice!) * 100
    : 0;
}

export function calculateOrderValues(
  order: Order,
  discountPercentage = 0,
  depositPercentage = 10
) {
  const fullPrice = order.items!.reduce((total, item) => {
    return total + calculateOrderItemPrice(item);
  }, 0);

  const totalPrice = fullPrice * (1 - discountPercentage / 100);
  const totalDeposit = (totalPrice * depositPercentage) / 100;
  const totalHold = totalPrice - totalDeposit;

  return {
    fullPrice,
    totalPrice: fullPrice - (fullPrice * discountPercentage) / 100,
    totalDiscount: (fullPrice * discountPercentage) / 100,
    totalDeposit,
    totalHold,
  };
}

export function setOrderItem(items: OrderItem[], item: OrderItem) {
  if (item.units! <= 0) {
    return items.filter((i) => i.product?.uuid !== item.product?.uuid);
  }
  const existingItem = items.find(
    (i) => i.product?.uuid === item.product?.uuid
  );
  if (existingItem) {
    return items.map((i) =>
      i.product?.uuid === item.product?.uuid ? item : i
    );
  }
  return [...(items || []), item];
}

export function orderUtils() {
  return {
    panelCapacity,
    calculateOrderValues,
    calculateOrderItemPrice,
  };
}
